'use strict';
import {extendPrototypeResult as localforage} from 'localforage-indexes';
import isObject from 'lodash/isObject';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/mapTo';
import 'rxjs/add/operator/skip';
import {Observable} from 'rxjs/Observable';
import {buildInjector} from '../../../_libs/angular-utils/index';
import {applyAutoFilter, epicErrorHandler, ReduxList} from '../../../_libs/redux-observable-list/index';
import {buildParentChannelSyncNotifier} from '../../../auth/utils/redux-lists';
import {BASE_REDUX_LIST_OPTIONS} from '../../../config/redux-list/index';
import {getCollection} from '../../../core-utilities/domain/ReduxUtils';
import {FilterModalOptions} from '../../../core-utilities/filters/FilterModalOptions';
import {sortByProperty, sortByPropertyAccessor} from '../../../core-utilities/filters/filterUtils';
import {SortMethod} from '../../../core-utilities/filters/SortMethod';
import {applyDefaultReduxListComponentOptions} from '../../../core-utilities/redux/ReduxListContainer';
import {buildPolling$} from '../../../core-utilities/rx-utils';
import {COLLECTION as ASSET_TASK_COLLECTION} from '../../../tasks/domain/AssetTaskDomain';
import {COLLECTION as TASK_COLLECTION} from '../../../tasks/domain/TaskDomain';
import {TaskType} from '../../../tasks/enums/TaskType';
import filterTemplate from '../../../tasks/filters/tasks-details-filter.html';
import {
  ASSET_FILE_REQUIREMENT_COLLECTION,
  ASSET_FORM_REQUIREMENT_COLLECTION,
  FILE_REQUIREMENT_COLLECTION,
  FORM_REQUIREMENT_COLLECTION,
} from '../../domain/config';
import {buildListSupportEpics} from '../../epics/listSupportEpics';
import {logger} from '../logger';


// load angular dependencies:
const dependencies = {};
export const injector = buildInjector(dependencies, [
  'FirebaseUtil',
]);

export const SUB_HEADER_FORMS = 'SUB_HEADER_FORMS';
export const SUB_HEADER_FILES = 'SUB_HEADER_FILES';

const FORMS_HEADER = {
  id: SUB_HEADER_FORMS,
};

const FILES_HEADER = {
  id: SUB_HEADER_FILES,
};

export const defaultFilterOptions = {
  status: {
    cancelled: true,
    has_rejection: true,
    rejected: true,
    pending: true,
    in_progress: true,
    submitted: true,
    approved: true,
  },
};

export const defaultSortOptions = {
  currentMethod: SortMethod.ASCENDING.name,
  property: 'name',
};

export const filterModalOptions = new FilterModalOptions({
  filterTemplate,
  filterOptions: defaultFilterOptions,
  sortOptions: defaultSortOptions,
});

export const localForageConfig = {
  name: 'SwiftProjects',
  version: 3,
//  size        : 4980736, // Size of database, in bytes. WebSQL-only for now.
  storeName: 'redux-storage-reqs',
  description: 'Cache requirement lists',
  driver: 'lruStorage',
  cacheSize: 500,
  lruIndex: 'redux-storage-reqs-idx',
  migrations: [{
    version: 2,
    onUpdate: () => {
      // Version 1 lead to data corruption. Delete the cached data.
      return null;
    },
  }, {
    version: 3,
    onUpdate: () => {
      // Version 2 contained previous filter data. Delete the cached data.
      return null;
    },
  }],
};
export const asyncStorage = localforage.createInstance(localForageConfig);

export function buildReqList(options, targetOptions) {
  const taskType = targetOptions.taskType;
  const taskCollection = taskType === TaskType.ASSET ? ASSET_TASK_COLLECTION : TASK_COLLECTION;

  const matchesAutoFilter = applyAutoFilter(['name', 'description'], 1);

  const reduxList = new ReduxList({
    ...BASE_REDUX_LIST_OPTIONS,
    pageSize: 100,
    hasParentId: true,
    useDelete: true,
    useFiltering: true,
    useAutoFilter: true,
    useFilterOptions: true,
    useSortOptions: true,
    defaultFilterOptions,
    defaultSortOptions,
    isVisible: isRequirementsVisible,
    // applyFilterData
    applySortOptions,
    syncNotifier: buildSyncNotifier(taskCollection, 'requirements', taskType),
    logger,
    ...options,

    cacheOptions: {
      localForage: asyncStorage,
      getKey: parentId => `reqs-${parentId}`,
    },
  });

  // Inject the dependency into all epics so that we don't need to know which one(s) need(s) it.
  reduxList.epics.epics.forEach(epic => {
    epic.$inject = [
      'PersistentUploaderService',
    ];
  });

  const supportEpics = buildListSupportEpics({
    actions: reduxList.actions,
    getState: options.getState,
    taskType: targetOptions.taskType,
    viewName: targetOptions.viewName,
    logger,
  });

  reduxList.epics.addEpics(supportEpics);

  const componentOptions = applyDefaultReduxListComponentOptions(reduxList.options, {
    autoInit: false,
    filterModalOptions,
    listTransform: groupByType,
  });

  return {
    reduxList,
    componentOptions,
  };

  function isRequirementsVisible(requirement, filterData) {
    if (!isObject(requirement)) return false;
    if (!matchesAutoFilter(requirement, filterData.filter)) return false;
    try {
      if (requirement.isDeleting) return false;
      if (!filterOnRequirementStatus(requirement, filterData.filterOptions, taskType)) return false;

      return true;
    } catch (err) {
      logger.error({err, requirement, options: filterData.filterOptions}, 'error with isRequirementsVisible');
      return false;
    }
  }

  function filterOnRequirementStatus(requirement, options, taskType) {
    if (taskType !== TaskType.TEMPLATE) {
      if (!options.status[requirement.status]) return false;
    }

    return true;
  }
}

export function applySortOptions(list, sortOptions) {
  const method = SortMethod.valueOf(sortOptions.currentMethod) || SortMethod.ASCENDING;
  if (method === SortMethod.NONE) {
    return list.sort(sortByProperty('id', true));
  } else if (sortOptions.property === 'date') {
    return list.sort(sortByPropertyAccessor(getCurrentRequirementDate, method.val, true));
  } else {
    return list.sort(sortByProperty(sortOptions.property, method.val, true));
  }
}

function getCurrentRequirementDate(requirement) {
  if (requirement) {
    return requirement.lastUpdated;
  } // else return undefined
}

function groupByType(list) {
  const formResults = [];
  const fileResults = [];
  list.forEach(model => {
    switch (getCollection(model)) {
      case ASSET_FILE_REQUIREMENT_COLLECTION:
      case FILE_REQUIREMENT_COLLECTION:
        fileResults.push(model);
        break;

      case ASSET_FORM_REQUIREMENT_COLLECTION:
      case FORM_REQUIREMENT_COLLECTION:
        formResults.push(model);
        break;

    }
  });
  const formResultLen = formResults.length;
  const fileResultLen = fileResults.length;

  if (formResultLen > 0 && fileResultLen > 0) {
    const result = [];

    result.push(FORMS_HEADER);
    result.push(...formResults);

    result.push(FILES_HEADER);
    result.push(...fileResults);

    return result;

  } else if (formResultLen > 0) {
    return formResults;

  } else if (fileResultLen > 0) {
    return fileResults;

  } else {
    return [];
  }
}

function buildSyncNotifier(parentPath, childPath, taskType) {
  const message = `req syncNotifier TT=${taskType}`;
  const parentSyncNotifier = buildParentChannelSyncNotifier(parentPath, childPath, message);

  if (taskType === TaskType.TEMPLATE) {
    return parentSyncNotifier;
  }

  const options = {
    pollInterval: 30000,
    pollCount: 10,
  };

  return function (parentId, store, dependencies) {
    const PersistentUploaderService = dependencies.PersistentUploaderService;
    return Observable
      .merge(
        // Standard channel sync from server.
        parentSyncNotifier(parentId, store)
          .catch(epicErrorHandler(logger, message)),
        // Trigger list to also attempt to sync after any file uploads
        PersistentUploaderService
          .size$()
          .pairwise()
          // Only trigger when size goes down
          .filter(([prevSize, nextSize]) => nextSize < prevSize)
          .throttleTime(10000, void 0, {leading: false, trailing: true}),
      )
      // Apply long polling to both sources of sync triggers.
      .switchMap(() =>
        buildPolling$(options.pollInterval, options.pollCount)
      );
  };
}



// WEBPACK FOOTER //
// ./src/js/requirements/redux/config/reqListConfig.js